import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Linkedin, Twitter, Instagram, Facebook, Heart } from 'lucide-react';
import { services as siteServices } from '../mockData';

const Footer = () => {
  const quickLinks = [
    { name: 'Home', href: '#home' },
    { name: 'Services', href: '#services' },
    { name: 'Process', href: '#process' },
    { name: 'Portfolio', href: '#portfolio' },
    { name: 'Contact', href: '#contact' },
    // Real route — proper anchor for crawl + new tab safe
    { name: '3D Brand Universe', href: '/experience', external: true },
  ];

  const socialLinks = [
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Facebook, href: '#', label: 'Facebook' },
  ];

  const handleAnchor = (e, href) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      const el = document.getElementById(href.slice(1));
      if (el) el.scrollIntoView({ behavior: 'smooth' });
      // Update the URL hash without a page jump (helps direct linking)
      try {
        window.history.replaceState(null, '', href);
      } catch {
        /* ignore */
      }
    }
  };

  return (
    <footer
      className="relative bg-slate-950 border-t border-cyan-500/20 overflow-hidden"
      data-testid="site-footer"
    >
      {/* Background decorations */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-cyan-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand & About */}
          <div>
            <motion.div whileHover={{ scale: 1.04 }} className="mb-4">
              <div
                className="text-4xl font-bold tracking-wider"
                style={{ fontFamily: "'Orbitron', sans-serif" }}
              >
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 neon-text">
                  ICD
                </span>
              </div>
              <div className="text-sm text-cyan-400/70 mt-1">
                Innovative Custom Designs
              </div>
            </motion.div>
            <p className="text-gray-400 leading-relaxed mb-4 text-sm">
              Strategic brand identity, custom website design and digital
              marketing for ambitious businesses. We architect brands that
              dominate the digital future.
            </p>
            <a
              href="mailto:support@innovativecustomdesigns.co"
              className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors text-sm"
            >
              <Mail size={16} />
              support@innovativecustomdesigns.co
            </a>
          </div>

          {/* Services — real <a> links so Bing/Google can follow them */}
          <div>
            <h3 className="text-base font-bold text-white mb-6 relative inline-block">
              Services
              <div className="absolute -bottom-2 left-0 w-12 h-0.5 bg-gradient-to-r from-cyan-500 to-blue-500" />
            </h3>
            <ul className="space-y-2.5">
              {siteServices.map((s) => (
                <li key={s.id}>
                  <a
                    href="#services"
                    onClick={(e) => handleAnchor(e, '#services')}
                    className="text-gray-400 hover:text-cyan-400 transition-colors text-sm flex items-start gap-2 group"
                    data-testid={`footer-service-${s.id}`}
                  >
                    <span className="text-cyan-500/50 group-hover:text-cyan-400 transition-colors mt-0.5">
                      ›
                    </span>
                    <span>{s.title}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-base font-bold text-white mb-6 relative inline-block">
              Explore
              <div className="absolute -bottom-2 left-0 w-12 h-0.5 bg-gradient-to-r from-cyan-500 to-blue-500" />
            </h3>
            <ul className="space-y-2.5">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    onClick={(e) => handleAnchor(e, link.href)}
                    className="text-gray-400 hover:text-cyan-400 transition-colors text-sm flex items-center gap-2 group"
                  >
                    <span className="w-0 h-0.5 bg-cyan-500 group-hover:w-4 transition-all" />
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect */}
          <div>
            <h3 className="text-base font-bold text-white mb-6 relative inline-block">
              Connect
              <div className="absolute -bottom-2 left-0 w-12 h-0.5 bg-gradient-to-r from-cyan-500 to-blue-500" />
            </h3>
            <p className="text-gray-400 mb-5 text-sm">
              Follow ICD for the latest in digital branding, design and
              marketing strategy.
            </p>
            <div className="flex gap-3 mb-6">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <motion.a
                    key={social.label}
                    href={social.href}
                    whileHover={{ scale: 1.15, y: -2 }}
                    whileTap={{ scale: 0.9 }}
                    className="w-9 h-9 bg-slate-800/50 border border-cyan-500/30 rounded-lg flex items-center justify-center text-cyan-400 hover:bg-cyan-500/20 hover:border-cyan-500 transition-all neon-glow-icon"
                    aria-label={social.label}
                  >
                    <Icon size={16} />
                  </motion.a>
                );
              })}
            </div>
            <a
              href="#contact"
              onClick={(e) => handleAnchor(e, '#contact')}
              className="inline-block px-4 py-2 text-xs font-semibold tracking-[0.2em] uppercase bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/25 transition-colors"
              style={{ fontFamily: "'Orbitron', sans-serif" }}
            >
              Book free consultation
            </a>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-cyan-500/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm">
            <div className="text-gray-500">
              &copy; 2026 Innovative Custom Designs &middot; All rights reserved.
            </div>
            <div className="flex items-center gap-2 text-gray-500">
              <span>Crafted with</span>
              <motion.span
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
              >
                <Heart size={14} className="text-cyan-500 fill-cyan-500" />
              </motion.span>
              <span>and futuristic precision.</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
